import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Utils, Query, Builder } from '@react-awesome-query-builder/ui';
import {
  booleanOperators,
  buildRemoveConfirmOptions,
  config as builderConfig,
  numberOperators,
  settings as configSettings
} from '../config.js';
import {
  droppedFieldIds,
  hasUsableTree,
  normalizeChildrenKeys
} from './treeUtils';
import {
  checkReturnTypeOfFunc,
  resolveFunctionReturnType
} from './functionReturnType';
import { migrateFuncArity, sanitizeFuncArgs } from './funcArgSanitizer';
import { connect } from 'react-redux';
import { findIncompleteRules } from './incompleteRules';

const generateUniqueVariantId = () => {
  const timestamp = new Date().getTime();
  const randomValue = Math.random();
  const uniqueId = `${timestamp}_${randomValue}`;
  return uniqueId;
};

const QueryBuilder = ({
  queryValue = null,
  type = '',
  setVariantRenderData,
  typoIndex,
  variantIndex,
  functionIndex,
  variantInfo,
  isReadOnly,
  allowNewIfEmpty,
  blackListData = [],
  onDirtyField,
  metaData,
  isFieldWithFunction = false,
  applicableFields = null,
  disableActions,
  generationState,
  onHumanStringChange,
  headerInfo = null,
  // FIN-12070 — per-tab noun for the delete-confirmation dialog. Defaults to
  // "rule" so any caller that doesn't pass it keeps the original copy.
  itemNoun = 'rule'
}) => {
  const { inputType: triggeredOn = 'transaction' } = variantInfo || {};
  const { fields: allFields, functionNames, aggregateFunction } = metaData;

  const fields = useMemo(() => {
    return applicableFields || allFields?.[triggeredOn] || {};
  }, [triggeredOn, allFields, applicableFields]);

  const blackList = useMemo(
    () =>
      blackListData
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((f) => ({
          title: f.name,
          value: f.name
        })),
    [blackListData]
  );

  const allFunc = useMemo(() => {
    return (
      variantInfo?.function?.reduce((arr, o) => {
        // FIN-13351: resolve the aggregated field in the scope the FUNCTION declares
        // (args.applicableTo), not the variant's inputType. An activity-scoped function inside a
        // Transaction variant otherwise found no field, defaulted to 'boolean', lost its numeric
        // operators, and had its stored condition dropped by Utils.checkTree.
        // `applicableFields`, when the caller passes it, is already scope-resolved — keep it.
        const funcType = applicableFields
          ? checkReturnTypeOfFunc(o, applicableFields, aggregateFunction)
          : resolveFunctionReturnType(
              o,
              allFields,
              aggregateFunction,
              triggeredOn
            );

        return {
          ...arr,
          [o['name']]: {
            label: o['name'],
            operators:
              funcType === 'boolean' ? booleanOperators : numberOperators,
            type: funcType
          }
        };
      }, {}) || {}
    );
  }, [
    variantInfo?.function,
    applicableFields,
    allFields,
    triggeredOn,
    aggregateFunction
  ]);

  const configValue = useMemo(() => {
    return {
      ...builderConfig(
        blackList,
        variantInfo?.function,
        functionNames,
        isFieldWithFunction
      ),
      fields: {
        ...fields,
        ...(isFieldWithFunction && allFunc)
      },
      settings: {
        ...configSettings,
        // FIN-12070 — override the static confirm copy with the per-tab noun.
        ...buildRemoveConfirmOptions(itemNoun),
        showErrorMessage: true,
        setOpOnChangeField: ['keep', 'default'],
        canReorder: !isReadOnly,
        canRegroup: !isReadOnly,
        immutableGroupsMode: isReadOnly,
        immutableFieldsMode: isReadOnly,
        immutableOpsMode: isReadOnly,
        immutableValuesMode: isReadOnly && generationState !== 'generated',
        addRuleLabel: 'Add Condition'
      }
    };
  }, [
    blackList,
    variantInfo?.function,
    fields,
    isFieldWithFunction,
    isReadOnly,
    generationState,
    itemNoun
  ]);

  const treeValue = useMemo(() => {
    // FIN-13382 — `queryValue || <fresh group>` is not enough: a stored `{}` is TRUTHY, so it
    // was passed straight to Utils.loadTree, which throws "Can't load tree!" on an object with
    // no id and no type. That threw during render and unmounted the whole tab into FIN-11822's
    // TabErrorFallback ("Something went wrong rendering the Functions tab").
    //
    // `{}` is the shape the AFC typologies actually store for a function with no condition —
    // live keycloak-qa, scenario_typologies for scenario 1947: typology 382 (AFC 1891) all
    // three functions, 383 (AFC 2078) functions 0-1, and 384 (AFC 2012) function 1, which is
    // "Count of historical payments to a particular business", the function the ticket names.
    // The typologies that render fine either hold a real tree or omit the key entirely, and
    // an omitted key was always safe because `undefined` is falsy and took the fallback.
    const raw = hasUsableTree(queryValue)
      ? queryValue
      : { id: generateUniqueVariantId(), type: 'group' };
    // FIN-13190 — fold surplus operands into nested form BEFORE checkTree, which silently
    // drops every argument the config does not declare. SUM and MULTIPLY used to take up to
    // five, so without this, opening a rule saved back then and saving it again would quietly
    // write a two-operand rule that computes a different number.
    return Utils.checkTree(
      Utils.loadTree(migrateFuncArity(normalizeChildrenKeys(raw), configValue)),
      configValue
    );
  }, [queryValue, configValue, generateUniqueVariantId]);

  // FIN-13223 — checkTree above removes leaves whose field is not in the picker config and says
  // nothing, so the condition just renders blank. Name what it took.
  const droppedFields = useMemo(
    () =>
      hasUsableTree(queryValue)
        ? droppedFieldIds(
            normalizeChildrenKeys(queryValue),
            Utils.getTree(treeValue)
          )
        : [],
    [queryValue, treeValue]
  );

  const [tree, setTree] = useState(treeValue);
  const [config, setConfig] = useState(configValue);

  // Tracks the queryValue we last emitted via onChange. Lets us distinguish
  // our own writes (skip re-sync — Immutable tree is already correct) from
  // genuinely external writes (e.g. "Copy from Global Conditions" replacing
  // aggregationCondition with a fresh object — must re-seed local tree).
  const lastEmittedRef = useRef(queryValue);

  useEffect(() => {
    setConfig(configValue);
  }, [configValue]);

  useEffect(() => {
    if (queryValue === lastEmittedRef.current) return;
    setTree(treeValue);
  }, [queryValue, treeValue]);

  const renderBuilder = (props) => (
    <div className="query-builder-container">
      <div
        className={`query-builder qb-lite ${
          type === 'functionAggregation' &&
          // startsWith('##_TT_MM_QA_##') enables add rule button for QA/dev testing variants
          !variantInfo?.variantName?.startsWith('##_TT_MM_QA_##')
            ? 'builder-without-add'
            : ''
        }`}>
        <Builder {...props} />
      </div>
    </div>
  );

  useEffect(() => {
    const incompleteRules = findIncompleteRules(tree.toJS(), config);

    if (incompleteRules.length) {
      // FIN-13260 — name the offending leaf. The footer used to read "Incomplete rules found,
      // please complete it." with no attribution, so with an accordion of collapsed panels there
      // was no way to tell WHICH rule in WHICH function was blocking Save.
      // FIN-13455 — the footer used to render the raw technical field id/path
      // (e.g. `location.gps_location.country`) instead of the picker's friendly
      // label (e.g. "Location Gps Location Country"). Resolve through
      // config.fields — the same map the field picker itself renders from —
      // falling back to the raw id only when the field isn't in the config.
      const fields = Array.from(
        new Set(
          incompleteRules
            .map((r) => r.rule?.properties?.field)
            .filter((f) => !!f)
        )
      ).map((f) => config.fields?.[f]?.label || f);
      disableActions(true, true, fields);
    } else {
      disableActions(false, true, []);
    }
    // config is a dependency because isRuleIncomplete now consults it for the
    // declared argument list of a function on the left-hand side.
  }, [tree, config]);

  useEffect(() => {
    if (!onHumanStringChange) {
      return;
    }
    try {
      const human = Utils.queryString(tree, config, true);
      onHumanStringChange(human || '');
    } catch (e) {
      onHumanStringChange('');
    }
  }, [tree, config, onHumanStringChange]);

  const onChange = (immutableTree, config) => {
    setTree(immutableTree);
    setConfig(config);
    // FIN-13190 — strip unset operand slots and arguments left behind by a
    // previously selected function before the tree leaves the builder.
    const jsonTree = sanitizeFuncArgs(Utils.getTree(immutableTree), config);
    // Mark this jsonTree as "ours" so the queryValue-sync useEffect doesn't
    // overwrite our just-set tree on the next render.
    lastEmittedRef.current = jsonTree;
    // Utils.validateTree(immutableTree)
    setVariantRenderData((prev) => {
      let dataToSet = prev;
      if (type === 'functionAggregation' || type === 'condition') {
        dataToSet[typoIndex].variants[variantIndex][type] = jsonTree;
      }
      if (type === 'aggregationCondition') {
        dataToSet[typoIndex].variants[variantIndex].function[
          functionIndex
        ].args[type] = jsonTree;
      }
      return [...dataToSet];
    });
    onDirtyField();
  };

  // FIN-13382 — treat a tree-less `{}` exactly like an absent value here too, so a caller that
  // does not allow a fresh condition renders nothing instead of an empty builder.
  if (!hasUsableTree(queryValue) && !allowNewIfEmpty) {
    return <></>;
  }
  return (
    <>
      {droppedFields.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message={`${
            droppedFields.length === 1 ? 'Condition' : 'Conditions'
          } not loaded — ${
            droppedFields.length === 1 ? 'this field is' : 'these fields are'
          } not available in the field picker: ${droppedFields.join(', ')}`}
        />
      )}
      {headerInfo && (
        <div style={{ marginBottom: 4, paddingLeft: 4 }}>
          <Tooltip title={headerInfo}>
            <InfoCircleOutlined
              style={{ color: '#8c8c8c', cursor: 'help', fontSize: 14 }}
            />
          </Tooltip>
        </div>
      )}
      <Query
        {...config}
        value={tree}
        onChange={onChange}
        renderBuilder={renderBuilder}
      />
    </>
  );
};

function mapStateToProps(state) {
  return {
    blackListData: state.trmDashboard.blackList?.data?.payload || [],
    metaData: state.amlsCommon.queryFields.data || {}
  };
}

export default connect(mapStateToProps, null)(QueryBuilder);
